import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AddOptionModal } from './AddOptionModal';
import { LoadingModal } from './LoadingModal';
import { BudgetVisualizationModal } from './BudgetVisualizationModal';

const partners = [
  {
    name: "ESPN",
    status: "preferred",
    statusText: "Preferred Partner - Pre-negotiated rates available"
  },
  {
    name: "Yahoo Sports",
    status: null
  },
  {
    name: "CBS Sportsline",
    status: null
  },
  {
    name: "Sports Interactive",
    status: "corporate",
    statusText: "Corporate Deal - Global partnership agreement"
  },
  {
    name: "NBA.com",
    status: null
  }
];

const generateRandomBudgets = (totalBudget) => {
  // Ensure some partners get $0 budget
  const budgets = partners.map(() => Math.random() > 0.3 ? Math.random() : 0);
  const sum = budgets.reduce((a, b) => a + b, 0);
  // Normalize to total budget
  return budgets.map(b => Math.round(b / sum * totalBudget));
};

const generateRandomKPI = () => {
  return (Math.random() * 5 + 2).toFixed(2); // Random KPI between 2 and 7
};

export function ScenarioDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const scenarioData = location.state?.scenarioData;
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [options, setOptions] = useState(() => {
    // Try to load existing options for this scenario from localStorage
    const savedOptions = localStorage.getItem(`scenario_options_${id}`);
    return savedOptions ? JSON.parse(savedOptions) : [];
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visualizationOption, setVisualizationOption] = useState(null);

  // Save options to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`scenario_options_${id}`, JSON.stringify(options));
    
    // Update the scenario's options count in the scenarios list
    const savedScenarios = JSON.parse(localStorage.getItem('scenarios') || '[]');
    const updatedScenarios = savedScenarios.map(scenario => {
      if (scenario.id === Number(id)) {
        return {
          ...scenario,
          optionsCount: options.length
        };
      }
      return scenario;
    });
    localStorage.setItem('scenarios', JSON.stringify(updatedScenarios));
  }, [options, id]);

  const handleAddOption = (optionData) => {
    setIsModalOpen(false);
    setIsLoading(true);

    setTimeout(() => {
      const randomBudgets = generateRandomBudgets(optionData.totalBudget);
      const newOption = {
        id: options.length + 1,
        totalBudget: optionData.totalBudget,
        minValue: optionData.minValue,
        maxValue: optionData.maxValue,
        recommendations: partners.map((partner, index) => ({
          partner,
          budget: randomBudgets[index],
          kpi: generateRandomKPI()
        }))
      };
      setOptions([...options, newOption]);
      setIsLoading(false);
    }, 3000);
  };

  const handleOptionSelect = (optionId) => {
    setSelectedOption(selectedOption === optionId ? null : optionId);
  };

  const handleAddToConsiderationSet = () => {
    // Get the selected option's data
    const selectedOptionData = options.find(opt => opt.id === selectedOption);
    
    // Filter partners with non-zero budgets
    const partnersToAdd = selectedOptionData.recommendations
      .filter(rec => rec.budget > 0)
      .map(rec => ({
        partner: rec.partner.name,
        hasData: false
      }));

    // Store partners in localStorage to pass them to RFP Scorecard
    const existingPartners = JSON.parse(localStorage.getItem('scorecardPartners') || '[]');
    const newPartners = [...existingPartners];
    
    // Only add partners that aren't already in the scorecard
    partnersToAdd.forEach(partner => {
      if (!existingPartners.some(p => p.partner === partner.partner)) {
        newPartners.push(partner);
      }
    });
    
    localStorage.setItem('scorecardPartners', JSON.stringify(newPartners));
    
    // Navigate to RFP Scorecard
    navigate('/partners/display/rfp-scorecard');
  };

  const getPartnerIcon = (status) => {
    if (status === 'preferred') {
      return '⭐'; // Star for preferred partner
    } else if (status === 'corporate') {
      return '🤝'; // Handshake for corporate deal
    }
    return null;
  };

  return (
    <main className="main-content">
      <h1 className="page-title">Direct Display Optimizer</h1>
      <div className="scenario-summary">
        <h2 className="scenario-title">
          {scenarioData?.name || "New Scenario"} - Scenario Information
        </h2>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">KPI:</span>
            <span className="summary-value">{scenarioData?.kpi || "Not Set"}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Time Period:</span>
            <span className="summary-value">
              {scenarioData ? `${scenarioData.startDate} - ${scenarioData.endDate}` : "Not Set"}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Channel Spend:</span>
            <span className="summary-value">
              ${scenarioData?.budget?.toLocaleString() || "Not Set"}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Audience:</span>
            <span className="summary-value">{scenarioData?.audience || "Not Set"}</span>
          </div>
        </div>
      </div>

      <div className="recommendations-section">
        <div className="section-header">
          <h2 className="section-title">Partner Recommendations</h2>
          <button 
            className="add-option-button"
            onClick={() => setIsModalOpen(true)}
          >
            Add Option
          </button>
        </div>

        <div className="table-container">
          {options.length > 0 ? (
            <>
              <table className="recommendations-table">
                <thead>
                  <tr>
                    <th rowSpan="2">Partner</th>
                    {options.map((option, index) => (
                      <th 
                        key={index}
                        colSpan="2" 
                        className={`option-header ${selectedOption === option.id ? 'selected' : ''}`}
                        onClick={() => handleOptionSelect(option.id)}
                      >
                        <div className="option-header-content">
                          <span>Option {option.id}</span>
                          <div className="option-actions">
                            <button 
                              className="view-distribution-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setVisualizationOption(option);
                              }}
                            >
                              View Distribution
                            </button>
                            <button 
                              className="remove-option-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const updatedOptions = options.filter(opt => opt.id !== option.id);
                                setOptions(updatedOptions);
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {options.map((option, index) => (
                      <React.Fragment key={index}>
                        <th 
                          className={`option-column ${selectedOption === option.id ? 'selected' : ''}`}
                          onClick={() => handleOptionSelect(option.id)}
                        >
                          Recommended Budget
                        </th>
                        <th 
                          className={`option-column ${selectedOption === option.id ? 'selected' : ''}`}
                          onClick={() => handleOptionSelect(option.id)}
                        >
                          KPI Result
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner, partnerIndex) => (
                    <tr key={partnerIndex}>
                      <td>
                        <div className="partner-cell">
                          <span>{partner.name}</span>
                          {partner.status && (
                            <span className="partner-status" title={partner.statusText}>
                              {getPartnerIcon(partner.status)}
                            </span>
                          )}
                        </div>
                      </td>
                      {options.map((option, optionIndex) => {
                        const rec = option.recommendations[partnerIndex];
                        return (
                          <React.Fragment key={optionIndex}>
                            <td 
                              className={`budget-cell ${selectedOption === option.id ? 'selected' : ''}`}
                              onClick={() => handleOptionSelect(option.id)}
                            >
                              ${rec.budget.toLocaleString()}
                            </td>
                            <td 
                              className={`kpi-cell ${selectedOption === option.id ? 'selected' : ''}`}
                              onClick={() => handleOptionSelect(option.id)}
                            >
                              {rec.kpi}%
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {selectedOption && (
                <div className="consideration-set-action">
                  <button 
                    className="button-primary"
                    onClick={handleAddToConsiderationSet}
                  >
                    Add to Consideration Set
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-options">
              No options added yet. Click "Add Option" to create your first option.
            </div>
          )}
        </div>
      </div>

      <AddOptionModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddOption}
      />

      {isLoading && (
        <LoadingModal message="Optimising budgets..." />
      )}

      <BudgetVisualizationModal 
        isOpen={visualizationOption !== null}
        onClose={() => setVisualizationOption(null)}
        option={visualizationOption}
      />
    </main>
  );
} 